import { useStore } from '../../store/useStore';
import { FiTrash2, FiEdit } from 'react-icons/fi';
import type { Highlight, Note } from '../../types';

interface HighlightsPanelProps {
  onJumpToHighlight?: (highlight: Highlight) => void;
  // PM 결정(2026-09-14): 메모 항목은 클릭 핸들러가 아예 없어서 눌러도 이동이 안 됐다.
  onJumpToNote?: (note: Note) => void;
}

const HighlightsPanel = ({ onJumpToHighlight, onJumpToNote }: HighlightsPanelProps) => {
  const { currentBook, highlights, notes, removeHighlight, removeNote } = useStore();

  if (!currentBook) return null;

  const bookHighlights = highlights.filter((h) => h.bookId === currentBook.id);
  const bookNotes = notes.filter((n) => n.bookId === currentBook.id);

  const getColorClass = (color: string) => {
    const colorMap: Record<string, string> = {
      yellow: 'bg-highlight-yellow',
      green: 'bg-highlight-green',
      blue: 'bg-highlight-blue',
      pink: 'bg-highlight-pink',
      purple: 'bg-highlight-purple',
    };
    return colorMap[color] || 'bg-gray-200';
  };

  const formatDate = (date: Date) => {
    return new Date(date).toLocaleDateString('ko-KR', {
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <div className="bg-white dark:bg-gray-800 w-80 h-full shadow-xl overflow-y-auto">
      {/* Header */}
      <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-4 py-4 flex items-center justify-between z-10">
        <h2 className="text-xl font-bold text-gray-900 dark:text-white">
          하이라이트 & 메모
        </h2>
      </div>

      {/* Highlights */}
      <div className="p-4">
        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
          <div className="w-4 h-4 bg-gradient-to-r from-yellow-300 to-pink-300 rounded"></div>
          <span>하이라이트 ({bookHighlights.length})</span>
        </h3>

        {bookHighlights.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-8">
            하이라이트가 없습니다
          </p>
        ) : (
          <div className="space-y-3">
            {bookHighlights.map((highlight) => (
              <div
                key={highlight.id}
                onClick={() => onJumpToHighlight?.(highlight)}
                className="group cursor-pointer p-3 rounded-lg border border-gray-200 dark:border-gray-700 hover:shadow-md transition-all"
              >
                <div className="flex items-start gap-2 mb-2">
                  <div className={`w-3 h-3 ${getColorClass(highlight.color)} rounded mt-1 flex-shrink-0`}></div>
                  <p className="text-sm text-gray-900 dark:text-white flex-1">
                    {highlight.text}
                  </p>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      removeHighlight(highlight.id);
                    }}
                    className="opacity-0 group-hover:opacity-100 p-1 hover:bg-red-50 dark:hover:bg-red-900 text-red-600 rounded transition-all"
                  >
                    <FiTrash2 size={14} />
                  </button>
                </div>
                {highlight.note && (
                  <div className="ml-5 mt-2 p-2 bg-gray-50 dark:bg-gray-900 rounded text-xs text-gray-600 dark:text-gray-400">
                    💭 {highlight.note}
                  </div>
                )}
                <div className="ml-5 mt-2 text-xs text-gray-400">
                  {formatDate(highlight.createdAt)}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Notes */}
      <div className="p-4 border-t border-gray-200 dark:border-gray-700">
        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
          <FiEdit size={16} />
          <span>메모 ({bookNotes.length})</span>
        </h3>

        {bookNotes.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-8">
            메모가 없습니다
          </p>
        ) : (
          <div className="space-y-3">
            {bookNotes.map((note) => (
              <div
                key={note.id}
                onClick={() => onJumpToNote?.(note)}
                className={`group p-3 rounded-lg border border-gray-200 dark:border-gray-700 hover:shadow-md transition-all ${onJumpToNote ? 'cursor-pointer' : ''}`}
              >
                <div className="flex items-start justify-between mb-2">
                  <p className="text-xs text-gray-500 dark:text-gray-400 italic">
                    "{note.text}"
                  </p>
                  <button
                    onClick={(e) => {
                      // PM 결정(2026-09-14): 행 전체가 이동 클릭이 됐으니 삭제 버튼은
                      // 버블링을 막아야 한다(하이라이트 행은 이미 이렇게 돼 있었다).
                      e.stopPropagation();
                      removeNote(note.id);
                    }}
                    className="opacity-0 group-hover:opacity-100 p-1 hover:bg-red-50 dark:hover:bg-red-900 text-red-600 rounded transition-all"
                  >
                    <FiTrash2 size={14} />
                  </button>
                </div>
                <p className="text-sm text-gray-900 dark:text-white mb-2">
                  {note.content}
                </p>
                <div className="text-xs text-gray-400">
                  {formatDate(note.createdAt)}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default HighlightsPanel;
